"use client";

import {
  LAUNCH_CASE_ID_STORAGE_KEY,
  LAUNCH_CASE_NAME_STORAGE_KEY,
  RouteContext,
  RouteContextValues,
  RouteData,
  readLaunchCaseNameFromSession,
} from "../contexts/routeContext";
import type { SettingsView } from "../contexts/routeContext";
import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { fetchCaseName } from "@/lib/threadDocumentOps";
import {
  ThreadContext,
  ThreadContextValues,
  ThreadData,
  ThreadListContext,
  ThreadListContextValues,
  ThreadListData,
  ThreadDocsContext,
  ThreadDocsContextValues,
  ThreadDocsData,
} from "../contexts/threadContext";
import { Assistant } from "@/components/assistant";

export function RouteContextWrapper({
  resolvedParams,
  resolvedSearchParams,
  chatsView = false,
  settingsView,
  settingsSkillId,
}: {
  resolvedParams: { caseId: string; threadId?: string | undefined };
  resolvedSearchParams: {
    docIds?: string;
    showThreadList?: boolean | undefined;
    prompt?: string | undefined;
  };
  chatsView?: boolean;
  settingsView?: SettingsView;
  settingsSkillId?: string;
}) {
  // Immutable session-level caseId. Computed once from sessionStorage (or the
  // first non-default URL param) and never changes during the tab lifetime.
  // biome-ignore lint/correctness/useExhaustiveDependencies: launchCaseId is intentionally session-scoped.
  const launchCaseId = useMemo(() => {
    if (typeof window === "undefined") return undefined;
    const stored = sessionStorage.getItem(LAUNCH_CASE_ID_STORAGE_KEY);
    if (stored !== null) return stored || undefined; // "" → undefined (launched as default)
    // First mount in this session — persist the launch value.
    const initial =
      resolvedParams.caseId === "default" ? "" : resolvedParams.caseId;
    sessionStorage.setItem(LAUNCH_CASE_ID_STORAGE_KEY, initial);
    return initial || undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const launchCaseName = useMemo(() => readLaunchCaseNameFromSession(), []);

  const [routeData, setRouteData] = useState<RouteData>(() => {
    let prompt: string | undefined;
    let promptSource: "hash" | "query" | undefined;

    if (resolvedParams.threadId === undefined) {
      const fromHash = extractPromptFromHash();
      if (fromHash) {
        prompt = fromHash.value;
        promptSource = fromHash.source;
      } else {
        prompt = decodePromptParam(resolvedSearchParams.prompt);
        promptSource = prompt ? "query" : undefined;
      }
    }

    return {
      caseId:
        resolvedParams.caseId === "default" ? undefined : resolvedParams.caseId,
      launchCaseId,
      launchCaseName,
      threadId: resolvedParams.threadId,
      docIds: extractDocIds(resolvedSearchParams.docIds),
      showThreadList:
        resolvedSearchParams.showThreadList?.toString() !== "false",
      lastThreadId: undefined,
      loadingThreadFromUrl:
        resolvedParams.threadId !== undefined &&
        !resolvedParams.threadId?.startsWith("__LOCAL"),
      prompt,
      promptSource,
      chatsView,
      settingsView,
      settingsSkillId,
    };
  });

  // Fetch the case name once per session when a case context exists but
  // sessionStorage doesn't have it yet. Subsequent mounts pick it up
  // synchronously via the readLaunchCaseNameFromSession() memo above.
  useEffect(() => {
    if (!launchCaseId) return;
    if (sessionStorage.getItem(LAUNCH_CASE_NAME_STORAGE_KEY)) return;
    let cancelled = false;
    fetchCaseName(launchCaseId).then((name) => {
      if (cancelled || !name) return;
      try {
        sessionStorage.setItem(LAUNCH_CASE_NAME_STORAGE_KEY, name);
      } catch {
        /* ignore */
      }
      setRouteData((prev) =>
        prev.launchCaseName === name ? prev : { ...prev, launchCaseName: name },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [launchCaseId]);

  const onRouteStateChanged = useCallback((update: Partial<RouteData>) => {
    setRouteData((prev) => {
      const hasChange = (Object.keys(update) as (keyof RouteData)[]).some(
        (k) => update[k] !== prev[k],
      );
      return hasChange ? { ...prev, ...update } : prev;
    });
  }, []);

  // Sync incoming page params with state. Required because useState's lazy
  // initializer runs once on mount; if react-router re-uses the RouteContextWrapper
  // instance across param changes (e.g. /proj/threadA → /proj/threadB without
  // remount), the URL bar would show the new threadId
  // but routeContext.threadId would stay on the old one, AUI wouldn't switch,
  // and the visible thread wouldn't update. The guard below skips the sync
  // when state already matches the URL — this matters because RouteWatcher
  // also writes the URL via replaceState in response to AUI events, and we
  // don't want a feedback loop reverting AUI-initiated state to the URL value.
  const incomingCaseId =
    resolvedParams.caseId === "default" ? undefined : resolvedParams.caseId;
  const incomingThreadId = resolvedParams.threadId;
  const incomingDocIdsKey = resolvedSearchParams.docIds ?? "";
  const incomingShowThreadList =
    resolvedSearchParams.showThreadList?.toString() !== "false";
  const incomingSettingsSkillId = settingsSkillId;
  const lastSyncRef = useRef<{
    caseId: string | undefined;
    threadId: string | undefined;
    docIdsKey: string;
    showThreadList: boolean;
    settingsSkillId: string | undefined;
  }>({
    caseId: incomingCaseId,
    threadId: incomingThreadId,
    docIdsKey: incomingDocIdsKey,
    showThreadList: incomingShowThreadList,
    settingsSkillId: incomingSettingsSkillId,
  });
  useEffect(() => {
    const last = lastSyncRef.current;
    const paramsChanged =
      last.caseId !== incomingCaseId ||
      last.threadId !== incomingThreadId ||
      last.docIdsKey !== incomingDocIdsKey ||
      last.showThreadList !== incomingShowThreadList ||
      last.settingsSkillId !== incomingSettingsSkillId;
    if (!paramsChanged) return;
    lastSyncRef.current = {
      caseId: incomingCaseId,
      threadId: incomingThreadId,
      docIdsKey: incomingDocIdsKey,
      showThreadList: incomingShowThreadList,
      settingsSkillId: incomingSettingsSkillId,
    };
    setRouteData((prev) => {
      if (
        prev.caseId === incomingCaseId &&
        prev.threadId === incomingThreadId &&
        prev.showThreadList === incomingShowThreadList &&
        prev.settingsSkillId === incomingSettingsSkillId
      ) {
        return prev;
      }
      return {
        ...prev,
        caseId: incomingCaseId,
        threadId: incomingThreadId,
        docIds: extractDocIds(incomingDocIdsKey),
        showThreadList: incomingShowThreadList,
        lastThreadId: prev.threadId,
        loadingThreadFromUrl:
          incomingThreadId !== undefined &&
          !incomingThreadId.startsWith("__LOCAL") &&
          incomingThreadId !== prev.threadId,
        settingsSkillId: incomingSettingsSkillId,
      };
    });
  }, [
    incomingCaseId,
    incomingThreadId,
    incomingDocIdsKey,
    incomingShowThreadList,
    incomingSettingsSkillId,
  ]);

  const routeContextValue = useMemo<RouteContextValues>(
    () => ({
      ...routeData,
      onRouteStateChanged,
    }),
    [routeData, onRouteStateChanged],
  );

  // --- Thread List state (threadItems, isListLoading) ---
  const [threadListData, setThreadListData] = useState<ThreadListData>(() => ({
    threadItems: [],
    isListLoading: true,
  }));

  const onThreadListChanged = useCallback((update: Partial<ThreadListData>) => {
    setThreadListData((prev) => {
      const hasChange = (Object.keys(update) as (keyof ThreadListData)[]).some(
        (k) => update[k] !== prev[k],
      );
      return hasChange ? { ...prev, ...update } : prev;
    });
  }, []);

  const threadListContextValue = useMemo<ThreadListContextValues>(
    () => ({
      ...threadListData,
      onThreadListChanged,
    }),
    [threadListData, onThreadListChanged],
  );

  // --- Thread Docs state (selectedDocs) ---
  const seedCaseId =
    resolvedParams.caseId === "default" ? null : resolvedParams.caseId;
  const urlDocIds = extractDocIds(resolvedSearchParams.docIds);

  const [threadDocsData, setThreadDocsData] = useState<ThreadDocsData>(() => ({
    // When launching with a caseId but no URL docs, start with [] — the scope
    // is already wildcard for the whole case; no need to populate individual doc
    // pills. Only populate when explicit docIds are in the URL.
    selectedDocs:
      urlDocIds.length > 0
        ? urlDocIds.map((docId) => ({
            docId,
            name: undefined,
            caseId: seedCaseId,
          }))
        : [],
  }));

  const onThreadDocsChanged = useCallback((update: Partial<ThreadDocsData>) => {
    setThreadDocsData((prev) => {
      const hasChange = (Object.keys(update) as (keyof ThreadDocsData)[]).some(
        (k) => update[k] !== prev[k],
      );
      return hasChange ? { ...prev, ...update } : prev;
    });
  }, []);

  const threadDocsContextValue = useMemo<ThreadDocsContextValues>(
    () => ({
      ...threadDocsData,
      onThreadDocsChanged,
    }),
    [threadDocsData, onThreadDocsChanged],
  );

  // --- Legacy combined context (for customThreadListProvider + routeWatcher) ---
  const onThreadStateChanged = useCallback(
    (update: Partial<ThreadData>) => {
      // Route updates to the appropriate split context
      const listUpdate: Partial<ThreadListData> = {};
      const docsUpdate: Partial<ThreadDocsData> = {};
      if ("threadItems" in update) listUpdate.threadItems = update.threadItems;
      if ("isListLoading" in update)
        listUpdate.isListLoading = update.isListLoading;
      if ("selectedDocs" in update)
        docsUpdate.selectedDocs = update.selectedDocs;
      if (Object.keys(listUpdate).length > 0) onThreadListChanged(listUpdate);
      if (Object.keys(docsUpdate).length > 0) onThreadDocsChanged(docsUpdate);
    },
    [onThreadListChanged, onThreadDocsChanged],
  );

  const threadContextValue = useMemo<ThreadContextValues>(
    () => ({
      threadItems: threadListData.threadItems,
      isListLoading: threadListData.isListLoading,
      selectedDocs: threadDocsData.selectedDocs,
      onThreadStateChanged,
    }),
    [threadListData, threadDocsData, onThreadStateChanged],
  );

  return (
    <RouteContext.Provider value={routeContextValue}>
      <ThreadContext.Provider value={threadContextValue}>
        <ThreadListContext.Provider value={threadListContextValue}>
          <ThreadDocsContext.Provider value={threadDocsContextValue}>
            <Assistant />
          </ThreadDocsContext.Provider>
        </ThreadListContext.Provider>
      </ThreadContext.Provider>
    </RouteContext.Provider>
  );
}

export function extractDocIds(docIds: string | undefined): string[] {
  if (!docIds) {
    return [];
  }

  const decodedDocIds = decodeURIComponent(docIds);

  const extractedDocIds = decodedDocIds?.includes(",")
    ? decodedDocIds.split(",").map((id) => id.trim())
    : decodedDocIds
      ? [decodedDocIds]
      : [];

  return extractedDocIds;
}

function decodePromptParam(prompt: string | undefined): string | undefined {
  if (!prompt) return undefined;
  try {
    return decodeURIComponent(prompt);
  } catch {
    return prompt;
  }
}

/**
 * Reads a `prompt` value from the URL hash fragment (`#prompt=<encoded>`).
 * Fragments are never sent to the server, so arbitrarily large prompts won't
 * trigger nginx 414 "Request-URI Too Large" errors.
 *
 * After reading, clears the hash via `replaceState` to prevent re-fire on refresh.
 * Returns `{ value, source }` so callers can log which delivery mechanism was used.
 */
export function extractPromptFromHash():
  | { value: string; source: "hash" }
  | undefined {
  if (typeof window === "undefined") return undefined;
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return undefined;

  // Support both `#prompt=<value>` and `#...&prompt=<value>&...`
  const params = new URLSearchParams(hash.slice(1));
  const raw = params.get("prompt");
  if (!raw) return undefined;

  // Clear the hash to prevent re-send on page refresh
  const url = new URL(window.location.href);
  url.hash = "";
  window.history.replaceState(null, "", url.pathname + url.search);

  try {
    return { value: decodeURIComponent(raw), source: "hash" };
  } catch {
    return { value: raw, source: "hash" };
  }
}
